import { useEffect, useState } from 'react';
import { INSTRUMENTS, showcaseScore, createScore, fixtureScore } from '@simplenotation/core';
import { Editor } from './ui/Editor';
import { loadScore, setState, getState, openModal, toast } from './state/store';
import { openScore, refreshMe, loadDraft } from './app/actions';
import { consumeAuthReturn } from './app/authReturn';

/** Set once the first route has loaded a document; before that the store holds its bootstrap score. */
let booted = false;

function usePath(): string {
  const [path, setPath] = useState(location.pathname + location.search);
  useEffect(() => {
    const on = () => setPath(location.pathname + location.search);
    window.addEventListener('popstate', on);
    return () => window.removeEventListener('popstate', on);
  }, []);
  return path;
}

/** `/new?instrument=<id>` (from the instrument landing pages) starts the score with that instrument; unknown ids fall back to guitar. */
function newScoreInstrument(url: URL): string {
  const id = url.searchParams.get('instrument');
  return id && INSTRUMENTS.some((i) => i.id === id) ? id : 'classical-guitar';
}

export function App() {
  const path = usePath();
  const url = new URL(path, location.origin);
  const slug = /^\/s\/([\w-]+)/.exec(url.pathname)?.[1];

  useEffect(() => {
    const back = consumeAuthReturn();
    void refreshMe().then(() => {
      if (!back) return;
      if ('error' in back) openModal('login', { mode: 'login', error: back.error });
      else if ('forgot' in back) openModal('login', { mode: 'forgot' });
      else toast(back.signedIn === 'signup' ? 'Account created. Your scores are saved to it.' : `Welcome back, ${getState().user?.name ?? ''}.`, 'success');
    });
  }, []);

  useEffect(() => {
    if (slug) {
      if (getState().doc.slug !== slug || getState().doc.status === 'error') void openScore(slug, url.searchParams.has('v') ? Number(url.searchParams.get('v')) : undefined);
    } else if (url.pathname === '/new') {
      const s = getState();
      if (url.searchParams.has('demo')) loadScore(showcaseScore());
      else if (url.searchParams.has('fixture')) loadScore(fixtureScore());
      else if (url.searchParams.has('draft')) loadScore(loadDraft() ?? createScore({ bars: 4 }));
      else if (s.doc.slug || !booted || url.searchParams.has('instrument')) loadScore(createScore({ title: 'Untitled', bars: 4, instrument: newScoreInstrument(url) }));
      setState({ mode: 'edit' });
      document.title = url.searchParams.has('demo') ? 'Demo score · SimpleNotation' : 'New score · SimpleNotation';
    } else document.title = 'SimpleNotation';
    booted = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  // The home page is a static document (landing.html) served outside the app.
  if (url.pathname === '/' || url.pathname === '/login') {
    location.replace('/');
    return null;
  }
  return <Editor />;
}
